/**
 * <TutorStudentRow> — un élève du masteur, en ligne de « Mes élèves » (organism).
 *
 * Toute la ligne ouvre sa fiche (`StudentsListItem.js`, dont les quatre gestes sont devenus les onglets
 * de la fiche, Wajih, 02/11). Elle dit qui il est et ce qu'il suit avec ce masteur, sur UNE ligne qui
 * ne passe à la suivante que faute de place (Wajih, 05/11) : son nom, sa classe, ses matières — la tête du
 * centre du live —, puis les activités finies aujourd'hui. Une matière par cours, avec sa séance
 * (`<SubjectBadge>`), jamais les matières des autres élèves du cours. Sans cours, ses matières
 * prioritaires, comme l'ancienne ligne.
 *
 * **Ses messages** : une bulle au bout de la ligne, dans son flux, à côté du bouton de la ligne et pas
 * dedans, avec le compte des non-lus quand des messages attendent ; la toucher ouvre la conversation
 * (`onOpenMessages`) — toujours là, comme l'ancien bouton « Messages », pour écrire le premier. La ligne,
 * elle, ouvre toujours la fiche (`onOpenProfile`) : lire son brief ne consomme pas ses non-lus.
 *
 * `selected` : c'est l'élève regardé (le cadre du live). Une ligne, pas une carte : la liste est UNE
 * carte-liste. Un élève porte le rond simple, jamais la capsule du tuteur.
 *
 * `compact` : la colonne des élèves de « Mes élèves » dans le cadre du live, plus calme et étroite — un visage
 * plus petit, son nom et sa classe, la pile de ses matières (sans les horaires), ses non-lus. Sa bulle n'y vient
 * qu'avec des non-lus : la conversation est à un onglet, juste à côté, et le nom garde sa place.
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Avatar } from "../../atoms/Avatar"
import { Badge } from "../../atoms/Badge"
import { Row } from "../../atoms/containers/Row"
import { Icon } from "../../atoms/icons/Icon"
import { SubjectBadge } from "../../atoms/SubjectBadge"
import { Text } from "../../atoms/typography/Text"
import { CardListRow } from "../../molecules/CardList"
import { SubjectBadges } from "../../molecules/SubjectBadges"
import { cappedCount } from "../../utils/cappedCount"
import { countLabel } from "../../../ports/primary/ui/formatters/textFormatter"
import { type UITutorStudent } from "../../../ports/primary/ui/types/tutorEleves"

/** L'avatar de la ligne — le 50 px du legacy, ramené à la grille. */
export const TUTOR_STUDENT_AVATAR_SIZE = 48
/** L'avatar de la ligne resserrée (la colonne des élèves). */
export const TUTOR_STUDENT_COMPACT_AVATAR_SIZE = 36

export type TutorStudentRowProps = {
    student: UITutorStudent
    /** Toucher la ligne : sa fiche. Absent, elle ne presse pas. */
    onOpenProfile?: () => void
    /** Toucher sa bulle : sa conversation. Absent, ou la ligne resserrée, la bulle ne se montre que pour compter des
     * non-lus. */
    onOpenMessages?: () => void
    /** Sa fiche est ouverte à côté. Défaut `false`. */
    selected?: boolean
    /** La ligne resserrée de la colonne des élèves (sans horaires ni compte du jour). Défaut `false`. */
    compact?: boolean
    testID?: string
}

export function TutorStudentRow(props: TutorStudentRowProps) {
    const { student, onOpenProfile, onOpenMessages, selected = false, compact = false, testID } = props
    const unread = student.unreadMessagesCount ?? 0
    const done = student.todayActivitiesCount ?? 0
    const courses = student.courses ?? []
    const badgeSubjects = courses.length > 0 ? courseSubjects(courses) : student.subjects

    return (
        <View style={[styles.line, selected ? styles.selected : undefined]} testID={testID}>
            <Pressable
                onPress={onOpenProfile}
                disabled={onOpenProfile == null}
                accessibilityRole={onOpenProfile != null ? "button" : undefined}
                accessibilityLabel={onOpenProfile != null ? `Ouvrir la fiche de ${student.name}` : undefined}
                accessibilityState={{ selected }}
                style={({ pressed }) => [styles.main, pressed ? styles.pressed : undefined]}
            >
                <CardListRow>
                    <Row gap="3" align="center">
                        <Avatar
                            uri={student.avatarUrl}
                            name={student.name}
                            size={compact ? TUTOR_STUDENT_COMPACT_AVATAR_SIZE : TUTOR_STUDENT_AVATAR_SIZE}
                            selected={selected}
                        />
                        <Row gap="2" align="center" style={styles.body}>
                            <Text size="text-md" weight="bold" color="primary" style={styles.shrink}>
                                {student.name}
                            </Text>
                            {student.classLabel != null && student.classLabel !== "" && (
                                <Text size="text-sm" color="text-muted">
                                    {student.classLabel}
                                </Text>
                            )}
                            {!compact && courses.length > 0
                                ? courses.map((course) => (
                                      <Row key={course.id} gap="1" align="center">
                                          <SubjectBadge subject={course.subject} showLabel />
                                          <Text size="text-sm" color="text-muted">
                                              {course.shortScheduleLabel}
                                          </Text>
                                          {course.status === "requested" && (
                                              <Text size="text-sm" color="text-warning">
                                                  demandé
                                              </Text>
                                          )}
                                      </Row>
                                  ))
                                : badgeSubjects.length > 0 && <SubjectBadges subjects={badgeSubjects} />}
                            {done > 0 && !compact && (
                                <Text size="text-xs" color="text-muted">
                                    {`${countLabel(done, "activité finie", "activités finies")} aujourd'hui`}
                                </Text>
                            )}
                        </Row>
                    </Row>
                </CardListRow>
            </Pressable>
            {/* À côté de la ligne, dans son flux : un bouton ne se pose pas dans un bouton. */}
            {(unread > 0 || (onOpenMessages != null && !compact)) && (
                <Pressable
                    onPress={onOpenMessages}
                    disabled={onOpenMessages == null}
                    accessibilityRole="button"
                    accessibilityLabel={
                        unread > 0
                            ? `Lire les messages de ${student.name}, ${unread} non lus`
                            : `Écrire à ${student.name}`
                    }
                    hitSlop={8}
                    style={styles.messages}
                >
                    <Row gap="1" align="center">
                        <Icon name="comment-dots" size={16} color={unread > 0 ? "primary" : "text-muted"} />
                        {unread > 0 && <Badge text={cappedCount(unread)} variant="danger" />}
                    </Row>
                </Pressable>
            )}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    line: { flexDirection: "row", alignItems: "center" },
    main: { flex: 1, minWidth: 0 },
    messages: { paddingRight: theme.spacing["4"] },
    selected: { backgroundColor: theme.colors["primary-soft"] },
    pressed: { backgroundColor: theme.colors["primary-soft"] },
    body: { flex: 1, minWidth: 0, flexWrap: "wrap", rowGap: theme.spacing["1"] },
    shrink: { flexShrink: 1 },
}))

/** Les matières de ses cours, une fois chacune : deux cours de maths font une pastille. */
function courseSubjects(courses: NonNullable<UITutorStudent["courses"]>) {
    const seen = new Set<string>()
    return courses
        .map((course) => course.subject)
        .filter((subject) => {
            const key = String(subject.id)
            if (seen.has(key)) return false
            seen.add(key)
            return true
        })
}
